import type { ClientItem } from 'owa-mail-store';
import type Item from 'owa-service/lib/contract/Item';
import { DecryptionState } from 'owa-mail-store/lib/store/schema/DecryptionState';
import { isAccountFeatureEnabled } from 'owa-feature-flags';

/**
 * Check if add-ins should be disabled during or after decryption for the given item.
 * Add-ins should be disabled when:
 * 1. Decryption is in progress (InProgress state)
 * 2. (addin-decryption only) Decryption is completed AND there is a decrypted body available
 *
 * Under addin-decryptionV1, add-ins are re-enabled once decryption completes.
 * Under addin-decryption only (V0), existing behavior is preserved: add-ins remain
 * disabled after decryption completes with content.
 *
 * @param item - The item to check (will be cast to ClientItem internally)
 * @returns true if add-ins should be disabled, false otherwise
 */
export default function shouldDisableAddinsForDecryption(item: Item): boolean {
    // Cast to ClientItem to access AddinData
    const clientItem = item as ClientItem;

    // Check if the addin-decryption feature is enabled
    if (!isAccountFeatureEnabled('addin-decryption', clientItem.MailboxInfo)) {
        return false;
    }

    const decryptionState = clientItem.AddinData?.DecryptionState;

    // Disable add-ins if decryption is in progress (applies to both V0 and V1)
    if (decryptionState === DecryptionState.InProgress) {
        return true;
    }

    // Under addin-decryptionV1, add-ins are re-enabled when decryption completes.
    if (isAccountFeatureEnabled('addin-decryptionV1', clientItem?.MailboxInfo)) {
        return false;
    }

    // Under addin-decryption only (V0), keep existing behavior: disable when completed with body.
    const hasDecryptedBody = !!clientItem.AddinData?.Body;
    if (decryptionState === DecryptionState.Completed && hasDecryptedBody) {
        return true;
    }

    return false;
}
